import { useEffect, useRef, useState } from 'react';
import {
  type LegField,
  seedEditingLeg,
  seedLegFocus,
  seedLegs,
  seedLegsState,
  seedRouteFromLive,
} from './leg_state.js';
import type { PlannerLeg, PlannerTraveler, PlannerTripType } from './planner_models.js';
import type { PlannerFormValues, PlannerFormProps } from './planner_form_model.js';

/*
 * O estado do formulário e os gestos que só mexem nele: a semente do mount, o
 * portão de rascunho, os retratos que o Cancelar dos popovers restaura, a troca
 * de aba e o Redefinir. Saíram de `planner_form.tsx` pelo teto de linhas dele.
 */

/** Gerador monotônico de keys de trecho. Key estável por trecho pra o React não
 *  reaproveitar a instância do combobox ao remover um do meio — senão o remanescente
 *  mostra o rótulo do removido. Prefixo distinto do seed (`leg-s*`). */
let legKeySeq = 0;
export function nextLegKey(): string {
  legKeySeq += 1;
  return `leg-k${String(legKeySeq)}`;
}

/**
 * Retrato ESTÁVEL do que faz o formulário "sujo": a aba, a rota, as datas do
 * corpo binário e a lista de trechos do multi — os mesmos campos que os dois
 * `search`/`search_multi` ecoam. Pura e fora do componente pelo mesmo motivo de
 * `shouldAskLegHeat`: o card já está no teto de complexidade, e comparar dois
 * retratos é o `useEffect` que usa isto, não esta extração.
 *
 * Rótulos (`originLabel`/`destinationLabel`) ficam de fora de propósito: são só
 * display, nunca vão no wire do submit — inclui-los sujaria o retrato por uma
 * mudança que a busca nem carrega.
 *
 * **Escopo do retrato: o que a FAIXA (band) cobre.** Viajantes/QUEM ficam de
 * fora porque a faixa não tem esse campo (ver `routeRow`) — um host futuro que
 * reusar `onDirtyChange` fora da faixa (o card aberto, por exemplo, que TEM
 * `TravelersField`) precisa revisitar este retrato antes de confiar nele.
 */
function draftSnapshot(p: {
  tripType: PlannerTripType;
  origin: string;
  destination: string;
  out: string | undefined;
  ret: string | undefined;
  legs: PlannerLeg[];
}): string {
  return JSON.stringify({
    tripType: p.tripType,
    origin: p.origin,
    destination: p.destination,
    out: p.out ?? null,
    ret: p.ret ?? null,
    legs: p.legs.map((l) => [l.origin, l.destination, l.date ?? null]),
  });
}

export function usePlannerState(
  initial: PlannerFormValues,
  todayIso: string,
  initialDatesOpen = false,
) {
  const [origin, setOrigin] = useState(initial.route.origin);
  const [destination, setDestination] = useState(initial.route.destination);
  const [originLabel, setOriginLabel] = useState(initial.route.originLabel);
  const [destinationLabel, setDestinationLabel] = useState(initial.route.destinationLabel);
  const [travelers, setTravelers] = useState<PlannerTraveler[]>(initial.travelers ?? []);
  const [policyTravelerId, setPolicyTravelerId] = useState<string | undefined>(
    initial.policyTravelerId,
  );
  const initialTripType: PlannerTripType = initial.tripType ?? 'round_trip';
  const [activeTripType, setActiveTripType] = useState<PlannerTripType>(initialTripType);
  const [legs, setLegs] = useState<PlannerLeg[]>(
    () => initial.legs ?? seedLegs(initial.route, initial.departDate),
  );
  const legsTouched = useRef(false);
  // O Redefinir (#2362) passou por aqui: o roteiro semeado pelo bloco
  // (`initial.legs`) já não existe, então o construtor volta a ser semeável pela
  // rota viva do binário, como um form sem `initial.legs` — ver `resetPlanner`.
  const legsCleared = useRef(false);
  const [legIds, setLegIds] = useState<string[]>(() =>
    legs.map((_, index) => `leg-s${String(index)}`),
  );
  // O pedido do host de abrir nas DATAS (#2136/#2129) vence a fila de pendências:
  // no multi ele mira a primeira perna, e o popover dela é `legFocus === 'date'`.
  const openOnDates = initialDatesOpen && initialTripType === 'multi_leg';
  const [editingLegId, setEditingLegId] = useState<string | null>(() =>
    openOnDates ? (legIds[0] ?? null) : seedEditingLeg(legs, legIds),
  );
  const [legFocus, setLegFocus] = useState<LegField | null>(() =>
    openOnDates ? 'date' : seedLegFocus(legs, legIds),
  );
  // A perna cujo popover de QUANDO está ABERTO. Separado de `legFocus` de
  // propósito: `legFocus` é quem PEDE a vez (o anel de atenção) e muda sozinho
  // conforme os campos são respondidos; o popover só abre por gesto no gatilho
  // da linha — ou pelo pedido explícito do host (`initialDatesOpen`).
  const [dateOpenLegId, setDateOpenLegId] = useState<string | null>(() =>
    openOnDates ? (legIds[0] ?? null) : null,
  );
  const validAnchor =
    initial.departDate && initial.departDate >= todayIso ? initial.departDate : undefined;
  const [out, setOut] = useState<string | undefined>(validAnchor);
  const [ret, setRet] = useState<string | undefined>(
    validAnchor && initialTripType !== 'one_way' ? initial.returnDate : undefined,
  );
  return {
    origin,
    setOrigin,
    destination,
    setDestination,
    originLabel,
    setOriginLabel,
    destinationLabel,
    setDestinationLabel,
    travelers,
    setTravelers,
    policyTravelerId,
    setPolicyTravelerId,
    activeTripType,
    setActiveTripType,
    legs,
    setLegs,
    legsTouched,
    legsCleared,
    legIds,
    setLegIds,
    editingLegId,
    setEditingLegId,
    legFocus,
    setLegFocus,
    dateOpenLegId,
    setDateOpenLegId,
    out,
    setOut,
    ret,
    setRet,
  };
}

export function useDraftGate(
  snapshot: Parameters<typeof draftSnapshot>[0],
  onDirtyChange: PlannerFormProps['onDirtyChange'],
) {
  const baseline = useRef(draftSnapshot(snapshot));
  const dirtyRef = useRef(false);
  useEffect(() => {
    const dirty = draftSnapshot(snapshot) !== baseline.current;
    if (dirty !== dirtyRef.current) {
      dirtyRef.current = dirty;
      onDirtyChange?.(dirty);
    }
  });
  return () => {
    baseline.current = draftSnapshot(snapshot);
    if (dirtyRef.current) {
      dirtyRef.current = false;
      onDirtyChange?.(false);
    }
  };
}

export type PlannerState = ReturnType<typeof usePlannerState>;

/** O mês paginado por linha do multi (`null` = o mês do piso da perna). */
export type LegPager = { legId: string | null; fromMonth: string | null };

/**
 * O popover de datas do layout ABERTO, controlado pelo form (#2136): abre por
 * gesto no gatilho do QUANDO ou já aberto a pedido do host (`initialDatesOpen`,
 * o segmento de datas da pill). Nunca abre SOZINHO — responder o destino não
 * puxa a data. Fora do layout aberto ele fica inerte.
 */
export function useWhenPopover(
  openLayout: boolean,
  initialOpen: boolean,
  initial: PlannerFormValues,
) {
  const [whenOpen, setWhenOpen] = useState(
    () => initialOpen && openLayout && (initial.tripType ?? 'round_trip') !== 'multi_leg',
  );
  return { whenOpen, setWhenOpen };
}

export function usePlannerSnapshots(state: PlannerState) {
  const datesAtOpen = useRef<{ out?: string; ret?: string }>({});
  const legsAtOpen = useRef<{
    legs: PlannerLeg[];
    ids: string[];
    editingId: string | null;
    focus: LegField | null;
  } | null>(null);
  return {
    openDates: () => {
      datesAtOpen.current = { out: state.out, ret: state.ret };
    },
    restoreDates: () => {
      state.setOut(datesAtOpen.current.out);
      state.setRet(datesAtOpen.current.ret);
    },
    openLegs: () => {
      legsAtOpen.current = {
        legs: state.legs,
        ids: state.legIds,
        editingId: state.editingLegId,
        focus: state.legFocus,
      };
    },
    restoreLegs: () => {
      const snapshot = legsAtOpen.current;
      if (!snapshot) return;
      state.setLegs(snapshot.legs);
      state.setLegIds(snapshot.ids);
      state.setEditingLegId(snapshot.editingId);
      state.setLegFocus(snapshot.focus);
    },
  };
}

export function useTripTypeSelection(
  state: PlannerState,
  initial: PlannerFormProps['initial'],
  resetLegPager: () => void,
) {
  return (type: PlannerTripType) => {
    if (type === 'one_way') state.setRet(undefined);
    // Semeia pela rota viva quando o roteiro não veio do bloco — ou quando o
    // Redefinir já o apagou (`legsCleared`): depois do gesto, o bloco deixou de
    // ser a fonte do construtor.
    const seedable = !initial.legs || state.legsCleared.current;
    if (type === 'multi_leg' && seedable && !state.legsTouched.current) {
      const live = {
        origin: state.origin,
        destination: state.destination,
        originLabel: state.originLabel,
        destinationLabel: state.destinationLabel,
      };
      const seeded = seedLegsState(
        seedRouteFromLive(live, initial.route),
        state.out,
        'leg-r',
        state.ret,
      );
      state.setLegs(seeded.legs);
      state.setLegIds(seeded.ids);
      state.setEditingLegId(seeded.openId);
      state.setLegFocus(seeded.focus);
      state.setDateOpenLegId(null);
      resetLegPager();
    }
    state.setActiveTripType(type);
  };
}

/**
 * O Redefinir da SAIA do multi (#2362): limpa TODOS os campos e devolve o
 * construtor à posição inicial — 2 trechos em branco, o primeiro pedindo a
 * origem. A rota e as datas do corpo binário saem junto (são os mesmos campos
 * vistos de outra aba; um "Ida e volta" ainda preenchido depois do Redefinir
 * diria que o gesto não limpou tudo). `legsTouched` volta a `false` e
 * `legsCleared` vira `true` de propósito: o construtor volta a ser semeável pela
 * rota viva, como no mount de um form sem `initial.legs` — mesmo num card que
 * abriu já como roteiro, porque o roteiro do bloco acabou de ser apagado (ver o
 * guard em `useTripTypeSelection`). O QUEM não entra: é o sujeito da viagem (o
 * requester semeado), não um campo da busca. Fora do form pelo teto de
 * complexidade de `PlannerForm`.
 */
export function resetPlanner(state: PlannerState, resetLegPager: () => void) {
  const seeded = seedLegsState({ origin: '', destination: '' }, undefined, nextLegKey());
  state.setOrigin('');
  state.setDestination('');
  state.setOriginLabel(undefined);
  state.setDestinationLabel(undefined);
  state.setOut(undefined);
  state.setRet(undefined);
  state.legsTouched.current = false;
  state.legsCleared.current = true;
  state.setLegs(seeded.legs);
  state.setLegIds(seeded.ids);
  state.setEditingLegId(seeded.openId);
  state.setLegFocus(seeded.focus);
  state.setDateOpenLegId(null);
  resetLegPager();
}
